/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { isBrowser } from '@/utils/browser';
import queryString from 'query-string';
import Styles from './CustomTabs.css';
import clsx from 'clsx';
import MobileOnlyCarousel from '@/components/atoms/MobileOnlyCarousel';
import kebabCase from 'lodash/kebabCase';

export const CustomTabsContext = createContext();

/**
 * @typedef {Object} Tab
 * @property {number} id - The unique identifier for the tab.
 * @property {string} href - The URL the tab links to.
 * @property {string} label - Tab label.
 * @property {boolean} navigate - Navigates towards another page.
 */

/**
 * @typedef {Record<string, Tab>} Tabs
 */

/**
 * @typedef {Object} CustomTabsProps
 * @property {Tabs} tabs - A collection of tabs.
 * @property {number} initialTab - The initial active tab ID.
 * @property {boolean} allowQueryParamCompare - This is useful when the 't' param is different than the key of the the Tab object
 * @property {(id: number) => void} [onTabChanged] - Callback triggered when the tab changes.
 */

/**
 * @param {CustomTabsProps} props
 */
function CustomTabs({
  initialTab,
  tabs,
  onTabChanged,
  allowQueryParamCompare,
  blockNavigation,
  ...props
}) {
  const embla = useRef();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState(initialTab ?? 0);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const getUrl = (base = '', tab = undefined) => {
    const extra = process.env.NODE_ENV === 'production' ? '.html' : '';
    const parsed = queryString.parse(queryString.extract(router.asPath));

    if (tab && tab.t) {
      parsed.t = tab.t;
    }

    if (tab && !tab.t) {
      delete parsed.t;
    }

    const str = queryString.stringify(parsed);

    if (!str || str.length === 0) {
      return `${base}${extra}`;
    }

    return `${base}${extra}?${str}`;
  };

  // Passing t and tabs as params to avoid delayed state stuff
  const findExpectedTab = (tParam = '', tabsInContext = tabs) => {
    const tQuery = tParam.toLowerCase();
    let tab = tabsInContext[tQuery];

    if (!tab && allowQueryParamCompare) {
      const tabKey = Object.keys(tabsInContext).find((k) => tabsInContext[k].t === tQuery);

      if (tabKey) {
        tab = tabsInContext[tabKey];
      }
    }

    return tab;
  };

  const tabItems = useMemo(() => {
    return Object.keys(tabs).map((k) => {
      const tabOrigin = tabs[k];
      return {
        ...tabOrigin,
      };
    });
  }, [tabs]);

  useEffect(() => {
    if (!isBrowser()) return;
    const parsed = queryString.parseUrl(router.asPath);

    if (parsed && parsed.query && parsed.query.t) {
      const expectedTab = findExpectedTab(parsed.query.t, tabs);
      if (!expectedTab) return;
      setActiveTab(expectedTab.id);
      return;
    }

    const path = router.pathname;
    const key = Object.keys(tabs).find((k) => tabs[k].href === path);

    if (key && path) {
      const expectedTab = tabs[key];
      if (expectedTab) {
        setActiveTab(expectedTab.id);
        return;
      }
    }
  }, []);

  useEffect(() => {
    const tabKey = Object.keys(tabs)
      .map((key) => {
        if (tabs[key].id === activeTab) {
          return key;
        }
        return undefined;
      })
      .filter((v) => !!v)
      .pop();

    const currentTab = tabs[tabKey];

    if (!currentTab || !tabKey) return;
    if (blockNavigation) return;

    router.replace(router.pathname, getUrl(currentTab.href, currentTab), {
      scroll: false,
      shallow: true,
    });
  }, [activeTab]);

  useEffect(() => {
    if (onTabChanged) {
      onTabChanged(activeTab);
    }

    if (!embla.current) return;
    embla.current.scrollTo(activeTab);
  }, [activeTab, onTabChanged]);

  return (
    <>
      <section className={clsx(props.className, 'custom-tabs', 'tabs-container')}>
        <MobileOnlyCarousel
          className="sf-tabs-scroll"
          customEmblaCarouselRef={embla}
          options={{
            align: 'center',
            slidesToScroll: 1,
            startIndex: activeTab,
            speed: 150,
          }}
          onSlideChanged={(index) => {
            handleTabChange(index);
          }}
          useThreshold
          threshold={576}
        >
          {({ className }) => (
            <>
              {tabItems.map((t) => {
                const key = `tab-id-sf-${t.id}`;
                const cn = clsx(
                  'tab-item',
                  className,
                  t.id === activeTab && 'active',
                  // this is for tag manager/seo only
                  `sf-tab-${kebabCase(t.label)}`,
                );

                const onClick = (ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  handleTabChange(t.id);
                };

                if (t.navigate) {
                  return (
                    <a key={key} className={cn} onClick={onClick} href={getUrl(t.href)}>
                      <p>{t.label}</p>
                      <span />
                    </a>
                  );
                }

                return (
                  <div key={key} className={cn} onClick={onClick}>
                    <p>{t.label}</p>
                    <span />
                  </div>
                );
              })}
            </>
          )}
        </MobileOnlyCarousel>
      </section>

      <style jsx>{Styles}</style>
    </>
  );
}

export default CustomTabs;
